import {
  type UploadAnalyticsCommonParams,
  type UploadAnalyticsContext,
  type UploadAnalyticsFailedStage,
  type UseTrackUploadAbortedParams,
  type UseTrackUploadCompletedParams,
  type UseTrackUploadErrorParams,
  type UseTrackUploadPartAbortedParams,
  type UseTrackUploadPartCompletedParams,
  type UseTrackUploadPartErrorParams,
  type UseTrackUploadPartStartedParams,
  type UseTrackUploadStartedParams,
} from '@air/analytics';
import { NetworkStatusInfo } from '@air/classes-network-status-info';
import {
  CHUNK_SIZE,
  isLarge,
  type Upload,
  uploadedPartsSelector,
  uploadingSpeedSelector,
  uploadProgressSelector,
} from '@air/redux-uploader';
import { getChunksCountForLargeFile, isRetryableUploadTransferError } from '@air/upload-utils';

import {
  metadataForPrivateUploadSelector,
  privateUploadBoardIdSelector,
} from '~/store/privateUploadMetadata/selectors';
import { metadataForPublicUploadSelector } from '~/store/publicUploadMetadata/selectors';
import { type AirState } from '~/store/types';

const getConnectivityState = () => (NetworkStatusInfo.isOnline ? 'online' : 'offline');

const getPathDepth = (path?: string) => {
  if (!path) {
    return undefined;
  }

  return path.split('/').filter(Boolean).length;
};

const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'Unknown upload error';
};

const getErrorName = (error: unknown) => {
  if (error instanceof Error) {
    return error.name;
  }
};

const getErrorStatusCode = (error: unknown) => {
  if (typeof error === 'object' && error !== null && 'status' in error && typeof error.status === 'number') {
    return error.status;
  }
};

const getUploadSizeBytes = (upload: Upload) => upload.apiUploadData.size ?? upload.file.size ?? 0;

const getUploadDurationMs = (upload: Upload) => Math.max((upload.completedAt ?? Date.now()) - upload.createdAt, 0);

const getPartCount = (upload: Upload) => (isLarge(upload) ? getChunksCountForLargeFile(upload) : 1);

const getPartSizeBytes = (upload: Upload, partNumber: number) => {
  const bytesBeforePart = (partNumber - 1) * CHUNK_SIZE;
  const remainingBytes = getUploadSizeBytes(upload) - bytesBeforePart;

  return Math.max(Math.min(remainingBytes, CHUNK_SIZE), 0);
};

const getPrivateUploadTarget = ({ boardId, isVersion }: { boardId?: string; isVersion?: boolean }) => {
  if (isVersion) {
    return 'asset_version';
  }

  if (boardId) {
    return 'board';
  }

  return 'workspace';
};

const getConnectivityStateForOnlineValue = (
  isOnline: boolean | undefined,
  fallback: UploadAnalyticsCommonParams['connectivity_state'],
) => {
  if (typeof isOnline !== 'boolean') {
    return fallback;
  }

  return isOnline ? 'online' : 'offline';
};

const getBaseUploadAnalyticsParams = ({
  context,
  path,
  state,
  upload,
}: {
  context: UploadAnalyticsContext;
  path?: string;
  state: AirState;
  upload: Upload;
}): Omit<UploadAnalyticsCommonParams, 'source' | 'upload_target'> => {
  const uploadProgress = uploadProgressSelector(state, upload.id);
  const uploadedParts = uploadedPartsSelector(state, upload.id);
  const pathDepth = getPathDepth(path);

  return {
    batch_id: upload.batchId,
    bytes_uploaded: upload.s3Info.totalBytesUploaded ?? uploadProgress.progress,
    chunk_size_bytes: isLarge(upload) ? CHUNK_SIZE : undefined,
    clip_id: upload.clipId || undefined,
    connectivity_state: getConnectivityState(),
    context,
    file_extension: upload.apiUploadData.ext,
    file_mime_type: upload.apiUploadData.mime,
    file_path_depth: pathDepth,
    file_size_bytes: getUploadSizeBytes(upload),
    file_type: upload.apiUploadData.type,
    has_folder_path: pathDepth ? pathDepth > 1 : false,
    image_height: upload.height,
    image_width: upload.width,
    is_large_upload: isLarge(upload),
    part_count: getPartCount(upload),
    upload_id: upload.id,
    upload_speed_bytes_per_second: uploadingSpeedSelector(state) ?? undefined,
    upload_status: upload.status,
    uploaded_part_count: uploadedParts.length,
  };
};

export const getPrivateUploadAnalyticsParams = ({
  context,
  state,
  upload,
}: {
  context: UploadAnalyticsContext;
  state: AirState;
  upload: Upload;
}): UploadAnalyticsCommonParams => {
  const uploadMetadata = metadataForPrivateUploadSelector(state, upload.id);
  const boardId = privateUploadBoardIdSelector(state, upload.id) ?? uploadMetadata?.originBoard?.id;
  const isVersion = uploadMetadata?.isVersion;

  return {
    ...getBaseUploadAnalyticsParams({ context, path: uploadMetadata?.filepath ?? upload.file.path, state, upload }),
    asset_id: uploadMetadata?.assetId,
    board_id: boardId,
    is_version: isVersion,
    source: 'private_upload',
    upload_target: getPrivateUploadTarget({ boardId, isVersion }),
    workspace_id: uploadMetadata?.workspaceId,
  };
};

export const getContentCollectionUploadAnalyticsParams = ({
  context,
  state,
  upload,
}: {
  context: UploadAnalyticsContext;
  state: AirState;
  upload: Upload;
}): UploadAnalyticsCommonParams => {
  const uploadMetadata = metadataForPublicUploadSelector(state, upload.id);

  return {
    ...getBaseUploadAnalyticsParams({ context, path: upload.file.path, state, upload }),
    session_id: uploadMetadata?.sessionId,
    short_board_id: uploadMetadata?.shortBoardId,
    source: 'content_collection',
    upload_target: 'content_collection',
  };
};

export const getUploadStartedAnalyticsParams = (
  commonParams: UploadAnalyticsCommonParams,
  upload: Upload,
  {
    maxRetryCount,
    retryCount = 0,
    uploadUrlType,
  }: {
    maxRetryCount?: number;
    retryCount?: number;
    uploadUrlType?: UseTrackUploadStartedParams['upload_url_type'];
  } = {},
): UseTrackUploadStartedParams => ({
  ...commonParams,
  max_retry_count: maxRetryCount,
  queued_duration_ms: Math.max(Date.now() - upload.createdAt, 0),
  retry_count: retryCount,
  start_reason: retryCount > 0 ? 'retry' : 'initial',
  started_after_url_fetch: true,
  upload_url_type: uploadUrlType,
});

export const getUploadCompletedAnalyticsParams = (
  commonParams: UploadAnalyticsCommonParams,
  upload: Upload,
  { uploadUrlType }: { uploadUrlType?: UseTrackUploadCompletedParams['upload_url_type'] } = {},
): UseTrackUploadCompletedParams => ({
  ...commonParams,
  upload_duration_ms: getUploadDurationMs(upload),
  upload_url_type: uploadUrlType,
});

export const getUploadErrorAnalyticsParams = ({
  commonParams,
  error,
  failedStage = 'unknown',
  upload,
  uploadUrlType,
}: {
  commonParams: UploadAnalyticsCommonParams;
  error: unknown;
  failedStage?: UploadAnalyticsFailedStage;
  upload: Upload;
  uploadUrlType?: UseTrackUploadErrorParams['upload_url_type'];
}): UseTrackUploadErrorParams => ({
  ...commonParams,
  error_message: getErrorMessage(error),
  error_name: getErrorName(error),
  error_status_code: getErrorStatusCode(error),
  failed_stage: failedStage,
  is_retryable: isRetryableUploadTransferError(error),
  upload_duration_ms: getUploadDurationMs(upload),
  upload_url_type: uploadUrlType,
});

export const getUploadAbortedAnalyticsParams = ({
  commonParams,
  failedStage = 'unknown',
  upload,
}: {
  commonParams: UploadAnalyticsCommonParams;
  failedStage?: UploadAnalyticsFailedStage;
  upload: Upload;
}): UseTrackUploadAbortedParams => ({
  ...commonParams,
  failed_stage: failedStage,
  upload_duration_ms: getUploadDurationMs(upload),
});

export const getUploadPartErrorAnalyticsParams = ({
  commonParams,
  error,
  isOnline,
  isRetryable,
  maxRetryCount,
  partNumber,
  retryCount,
  upload,
  uploadUrlType,
}: {
  commonParams: UploadAnalyticsCommonParams;
  error: unknown;
  isOnline?: boolean;
  isRetryable?: boolean;
  maxRetryCount?: number;
  partNumber: number;
  retryCount?: number;
  upload: Upload;
  uploadUrlType: UseTrackUploadPartErrorParams['upload_url_type'];
}): UseTrackUploadPartErrorParams => ({
  ...commonParams,
  connectivity_state: getConnectivityStateForOnlineValue(isOnline, commonParams.connectivity_state),
  error_message: getErrorMessage(error),
  error_name: getErrorName(error),
  error_status_code: getErrorStatusCode(error),
  is_retryable: isRetryable ?? isRetryableUploadTransferError(error),
  max_retry_count: maxRetryCount,
  part_number: partNumber,
  part_size_bytes: getPartSizeBytes(upload, partNumber),
  retry_count: retryCount,
  upload_url_type: uploadUrlType,
});

export const getUploadPartAbortedAnalyticsParams = ({
  abortReason = 'transfer_aborted',
  commonParams,
  error,
  isOnline,
  maxRetryCount,
  partNumber,
  retryCount,
  upload,
}: {
  abortReason?: UseTrackUploadPartAbortedParams['abort_reason'];
  commonParams: UploadAnalyticsCommonParams;
  error: unknown;
  isOnline?: boolean;
  maxRetryCount?: number;
  partNumber: number;
  retryCount?: number;
  upload: Upload;
}): UseTrackUploadPartAbortedParams => ({
  ...commonParams,
  abort_reason: abortReason,
  connectivity_state: getConnectivityStateForOnlineValue(isOnline, commonParams.connectivity_state),
  error_message: getErrorMessage(error),
  error_name: getErrorName(error),
  error_status_code: getErrorStatusCode(error),
  max_retry_count: maxRetryCount,
  part_number: partNumber,
  part_size_bytes: getPartSizeBytes(upload, partNumber),
  retry_count: retryCount,
});

export const getUploadPartCompletedAnalyticsParams = ({
  commonParams,
  isOnline,
  maxRetryCount,
  partNumber,
  partUploadDurationMs,
  retryCount,
  upload,
  uploadUrlType,
}: {
  commonParams: UploadAnalyticsCommonParams;
  isOnline?: boolean;
  maxRetryCount?: number;
  partNumber: number;
  partUploadDurationMs?: number;
  retryCount: number;
  upload: Upload;
  uploadUrlType: UseTrackUploadPartCompletedParams['upload_url_type'];
}): UseTrackUploadPartCompletedParams => ({
  ...commonParams,
  connectivity_state: getConnectivityStateForOnlineValue(isOnline, commonParams.connectivity_state),
  max_retry_count: maxRetryCount,
  part_number: partNumber,
  part_size_bytes: getPartSizeBytes(upload, partNumber),
  part_upload_duration_ms: partUploadDurationMs,
  retry_count: retryCount,
  upload_url_type: uploadUrlType,
});

export const getUploadPartStartedAnalyticsParams = ({
  commonParams,
  isOnline,
  maxRetryCount,
  partNumber,
  retryCount,
  upload,
  uploadUrlType,
}: {
  commonParams: UploadAnalyticsCommonParams;
  isOnline?: boolean;
  maxRetryCount?: number;
  partNumber: number;
  retryCount: number;
  upload: Upload;
  uploadUrlType: UseTrackUploadPartStartedParams['upload_url_type'];
}): UseTrackUploadPartStartedParams => ({
  ...commonParams,
  connectivity_state: getConnectivityStateForOnlineValue(isOnline, commonParams.connectivity_state),
  max_retry_count: maxRetryCount,
  part_number: partNumber,
  part_size_bytes: getPartSizeBytes(upload, partNumber),
  retry_count: retryCount,
  start_reason: retryCount > 0 ? 'retry' : 'initial',
  upload_url_type: uploadUrlType,
});
